/**
 * AOV Savings Ladder
 * Savings progress bar + exit intent popup
 * Depends on: pubsub.js (subscribe / PUB_SUB_EVENTS), global.js (trapFocus / removeTrapFocus)
 */

// ─── Tier config (single source of truth — amounts in cents) ──────────────────

const AOV_TIERS = [
  { threshold: 30000,  save: 20,  code: 'SAVE20'  },
  { threshold: 40000,  save: 30,  code: 'SAVE30'  },
  { threshold: 50000,  save: 50,  code: 'SAVE50'  },
  { threshold: 100000, save: 125, code: 'SAVE125' },
];

/** How close (in cents) to the next tier before the exit popup triggers */
const AOV_EXIT_TRIGGER_WINDOW = 15000; // $150

// ─── Pure helpers ─────────────────────────────────────────────────────────────

/**
 * Format cents as a dollar string without decimals.
 * e.g. 3000 → "$30"
 */
function aovFormatMoney(cents) {
  return '$' + Math.round(cents / 100);
}

/**
 * Compute the current savings state from a cart total (cents).
 * Returns:
 *   qualifiedTier    — highest tier already met (or null)
 *   nextTier         — next tier not yet met (or null if max reached)
 *   amountToNext     — cents needed to reach nextTier
 *   progressPercent  — 0–100, position within current segment
 *   isMaxTierReached — boolean
 */
function aovComputeState(totalCents) {
  let qualifiedTier = null;
  let nextTier = null;

  for (const tier of AOV_TIERS) {
    if (totalCents >= tier.threshold) {
      qualifiedTier = tier;
    } else {
      nextTier = tier;
      break;
    }
  }

  const isMaxTierReached = !nextTier;
  const amountToNext = nextTier ? nextTier.threshold - totalCents : 0;

  // Progress % within the current segment
  let progressPercent = 0;
  if (isMaxTierReached) {
    progressPercent = 100;
  } else {
    const prevThreshold = qualifiedTier ? qualifiedTier.threshold : 0;
    const segmentSize = nextTier.threshold - prevThreshold;
    const segmentProgress = totalCents - prevThreshold;
    progressPercent = Math.min(100, Math.round((segmentProgress / segmentSize) * 100));
  }

  return { qualifiedTier, nextTier, amountToNext, progressPercent, isMaxTierReached };
}

// ─── Savings Bar custom element ───────────────────────────────────────────────

class AovSavingsBar extends HTMLElement {
  connectedCallback() {
    this._bar    = this.querySelector('.aov-savings-bar');
    this._msg    = this.querySelector('.aov-savings-bar__message');
    this._track  = this.querySelector('.aov-savings-bar__track');
    this._fill   = this.querySelector('.aov-savings-bar__fill');
    this._total  = window.aovCartTotal || 0;

    this._render(this._total);

    // Subscribe to Dawn's existing pubsub cart-update event
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this._unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        const newTotal = event?.cartData?.total_price ?? this._total;
        this._total = newTotal;
        this._render(newTotal);

        // Bridge to exit popup — avoid a double pubsub subscription
        document.dispatchEvent(new CustomEvent('aov:cart-updated', {
          detail: { totalCents: newTotal },
        }));
      });
    }
  }

  disconnectedCallback() {
    if (this._unsubscribe) this._unsubscribe();
  }

  _render(totalCents) {
    if (!this._bar) return;

    const state = aovComputeState(totalCents);

    // Build message
    let message = '';
    if (state.isMaxTierReached) {
      message = `<strong>You've unlocked $${state.qualifiedTier.save} off</strong> your order — applied at checkout!`;
      this._bar.classList.add('aov-savings-bar--complete');
    } else if (state.qualifiedTier) {
      message = `You've unlocked <strong>$${state.qualifiedTier.save} off</strong>. `
              + `Add <strong>${aovFormatMoney(state.amountToNext)}</strong> more to save `
              + `<strong>$${state.nextTier.save}</strong>!`;
      this._bar.classList.remove('aov-savings-bar--complete');
    } else {
      message = `Add <strong>${aovFormatMoney(state.amountToNext)}</strong> to unlock `
              + `<strong>$${state.nextTier.save} off</strong> your order!`;
      this._bar.classList.remove('aov-savings-bar--complete');
    }

    this._msg.innerHTML = message;

    // Update fill width
    this._fill.style.width = state.progressPercent + '%';

    // Update ARIA
    this._track.setAttribute('aria-valuenow', state.progressPercent);

    // Reveal bar (removes display:none) — set width BEFORE revealing to prevent CLS
    this._bar.classList.remove('aov-savings-bar--hidden');
  }
}

// ─── Exit Popup custom element ────────────────────────────────────────────────

class AovExitPopup extends HTMLElement {
  connectedCallback() {
    // One popup per session
    if (sessionStorage.getItem('aov_popup_shown')) return;

    this._overlay  = this.querySelector('.aov-popup-overlay');
    this._dialog   = this.querySelector('[role="dialog"]');
    this._headline = this.querySelector('.aov-popup__headline');
    this._subtext  = this.querySelector('.aov-popup__subtext');
    this._checkoutBtn = this.querySelector('#aov-popup-checkout-btn');
    this._shown    = false;
    this._currentTotal = window.aovCartTotal || 0;

    if (!this._overlay) return;

    // Close actions
    this.querySelector('.aov-popup__close').addEventListener('click', () => this.close());
    this.querySelector('.aov-popup__btn--secondary').addEventListener('click', () => this.close());
    this._overlay.addEventListener('click', (e) => {
      if (e.target === this._overlay) this.close();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this._shown) this.close();
    });

    // Keep total in sync via the bridge event fired by AovSavingsBar
    document.addEventListener('aov:cart-updated', (e) => {
      this._currentTotal = e.detail.totalCents;
    });

    // Desktop: mouse leaves browser chrome (moving to another tab / address bar)
    document.documentElement.addEventListener('mouseleave', (e) => {
      if (e.clientY < 20) this._maybeShow();
    });

    // Mobile: tab becomes hidden (user switches app / checks notifications)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') this._maybeShow();
    });
  }

  _maybeShow() {
    if (this._shown) return;
    if (sessionStorage.getItem('aov_popup_shown')) return;

    // Never show to visitors with an empty cart
    if (!this._currentTotal || this._currentTotal <= 0) return;

    const state = aovComputeState(this._currentTotal);

    // Don't show if cart is at max tier or no next tier
    if (!state.nextTier) return;

    // Only show if within $150 of next tier
    if (state.amountToNext > AOV_EXIT_TRIGGER_WINDOW) return;

    // Personalise copy
    const needed = aovFormatMoney(state.amountToNext);
    const saving = '$' + state.nextTier.save;

    this._headline.textContent = 'Wait — you\'re so close!';
    this._subtext.innerHTML = `Add just <strong>${needed}</strong> more to unlock `
      + `<strong>${saving} off</strong> your entire order.`;

    // Checkout link
    if (state.qualifiedTier) {
      this._checkoutBtn.href = '/discount/' + state.qualifiedTier.code + '?redirect=/checkout';
    } else {
      this._checkoutBtn.href = '/checkout';
    }

    this.show();
  }

  show() {
    sessionStorage.setItem('aov_popup_shown', '1');
    this._shown = true;

    this._overlay.classList.add('is-open');
    this._overlay.setAttribute('aria-hidden', 'false');
    this._dialog.setAttribute('aria-hidden', 'false');
    document.body.classList.add('overflow-hidden');

    // Trap focus inside dialog
    if (typeof trapFocus !== 'undefined') {
      trapFocus(this._dialog);
    } else {
      this._dialog.focus();
    }
  }

  close() {
    this._overlay.classList.remove('is-open');
    this._overlay.setAttribute('aria-hidden', 'true');
    this._dialog.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('overflow-hidden');

    if (typeof removeTrapFocus !== 'undefined') {
      removeTrapFocus();
    }
  }
}

// ─── Register custom elements ─────────────────────────────────────────────────

customElements.define('aov-savings-bar', AovSavingsBar);
customElements.define('aov-exit-popup', AovExitPopup);
